import React from "react";

import { css, cx } from "@emotion/css";
import MuiTextField, { TextFieldClasses as MuiTextFieldClasses } from "@mui/material/TextField";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

export type BaseTextFieldProps = {
  value: string;
  /** Receives the value, not the event: atoms don't leak DOM types. */
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  name?: string;
  type?: "text" | "email" | "tel";
  inputMode?: "text" | "email" | "tel" | "numeric";
  autoComplete?: "email" | "tel" | "one-time-code" | "off";
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
  required?: boolean;
};

type TextFieldClasses = StyleClasses<keyof MuiTextFieldClasses>;

export type TextFieldProps = AtomProps<BaseTextFieldProps, TextFieldClasses>;

/**
 * TextField is a React functional component that renders a Material-UI TextField.
 *
 * It restricts a MUI TextField to a specific set of props:
 */
export const TextField: React.FC<TextFieldProps> = ({
  className,
  classes,
  onChange,
  inputMode,
  autoComplete,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <MuiTextField
      {...props}
      classes={classes}
      variant="outlined"
      size="small"
      onChange={event => onChange(event.target.value)}
      slotProps={{ htmlInput: { inputMode, autoComplete } }}
      className={cx(styles.textField, className)}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  // `injectGlobal` in index.tsx resets `input { all: unset }`, which strips
  // anything MUI does not declare itself — restore what the field needs.
  textField: css({
    "& .MuiInputBase-input": {
      font: "inherit",
      color: theme.palette.text.primary,
      cursor: "text",
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
  }),
});
